import SiteInertiaController from '@/actions/App/Http/Controllers/SiteInertiaController';
import { getImageUrl } from '@/lib/image';
import { type NavSite } from '@/types/search';
import { useMemo } from 'react';

export interface NavLink {
  key: string;
  title: string;
  /** One-line supporting copy. A mega-menu whose items are bare links is just a dropdown. */
  description?: string | null;
  href: string;
  /** Resolved location artwork; null falls back to a typographic tile. */
  image?: string | null;
  isExternal?: boolean;
}

export interface NavGroup {
  location: string;
  links: NavLink[];
}

export interface NavItem {
  title: string;
  href?: string;
  groups?: NavGroup[];
}

const EXPLORE_GROUP: NavGroup = {
  location: 'Liên kết Lustay',
  links: [
    {
      key: 'lustay',
      title: 'Lustay',
      description: 'Đặt phòng khách sạn & nghỉ dưỡng',
      href: 'https://lustay.com',
      isExternal: true,
    },
    {
      key: 'cam-nang',
      title: 'Cẩm nang du lịch',
      description: 'Kinh nghiệm và gợi ý cho chuyến đi',
      href: 'https://megagrandworld.com.vn/',
      isExternal: true,
    },
  ],
};

/**
 * Builds the header's nav model from the `navSites` shared prop.
 *
 * `navSites` comes from HandleWebInertiaRequests so it is present on every web page;
 * the `sites` fallback keeps older per-page payloads working during the transition.
 */
export function useNavItems(navSites?: NavSite[], fallbackSites?: NavSite[]): NavItem[] {
  return useMemo(() => {
    const sites = navSites ?? fallbackSites ?? [];

    const byLocation = sites.reduce<Record<string, NavLink[]>>((acc, site) => {
      if (!site.mapping_code || !site.name) return acc;

      const key = site.location?.name || 'Khác';
      const bucket = acc[key] ?? (acc[key] = []);

      bucket.push({
        key: String(site.id),
        title: site.name,
        description: site.address || site.location?.name || null,
        href: SiteInertiaController.search({ query: { site: site.mapping_code } }).url,
        image: site.location?.image ? getImageUrl(site.location.image, 'location') : null,
      });

      return acc;
    }, {});

    const groups: NavGroup[] = Object.entries(byLocation)
      .map(([location, links]) => ({ location, links }))
      .sort((a, b) => {
        // "Khác" is the catch-all bucket, so it always sorts last.
        if (a.location === 'Khác') return 1;
        if (b.location === 'Khác') return -1;
        return a.location.localeCompare(b.location, 'vi');
      });

    const items: NavItem[] = [];
    if (groups.length > 0) items.push({ title: 'Du lịch', groups });
    items.push({ title: 'Khám phá', groups: [EXPLORE_GROUP] });
    items.push({ title: 'Tin tức', href: '/tin-tuc' });

    return items;
  }, [navSites, fallbackSites]);
}
